/**
 * The share wizard's per-topic session store.
 *
 * One trip through the wizard is a session, and it survives things the React
 * tree does not: the SMS handoff (the OS foregrounds Messages and may suspend
 * the webview), the set-names detour to /profile, and a mid-flow reload. This
 * module is the whole of what that costs — the stored shape, and the
 * operations on it. `useWizardSession` is the hook that drives them, and
 * `startShareSession` is how every entry point in the app opens one.
 */
import { StorageService } from '@/services/storage';
import type { ContactList } from '@/modules/contact';
import type { MessageDraft, MessageHeader } from '@/modules/message';
import type { MessageIntent } from '@/modules/template';

// Bumped whenever restoring an older session could no longer make sense —
// loadPersistedWizardState discards state that does not match, which is the
// only migration this store has. v18 puts the leg TYPE on every queued draft
// (#447): a v17 session's drafts predate it, so restoring one would hand the
// server a leg with no type and the send would be rejected mid-handoff.
export const STORAGE_SCHEMA_VERSION = 18;

// Slim persisted form of a MessageDraft: stores only topicId (not the full
// Topic with base64 image blobs) to stay well within localStorage quota.
// The Provider re-fetches the full Topic on hydration.
export interface PersistedMessageDraft {
  header: {
    topicId: string;
    intent: MessageIntent;
    type: MessageHeader['type'];
    recipients: ContactList;
  };
  body: string;
}

export interface PersistedWizardState {
  schemaVersion: number;
  sendQueue: PersistedMessageDraft[];
  sendQueueCursor: number;
  inProgressLegId?: string;
  // Update-flow state (#427). A persisted aboutChangesSince doubles as the
  // update-mode signal for the sender returning from SMS, whose leg link
  // carries no update marker.
  updateSummary?: string;
  aboutChangesSince?: string;
  // Generic flow fields (#447), usable by any sharing flow that needs them —
  // not news-specific state, though news is today's only consumer:
  //   previousLegId  the leg that led the user here. It is also what puts
  //                  the wizard in its reshare sharing mode: a session
  //                  holding one is passing that leg on.
  previousLegId?: string;
  // The resharer's first-person connection to whoever sent them the leg they
  // pass on ("I work with"). One answer per session, asked once at the top of
  // the garden composer; a classic topic never asks it.
  connectionToPrevLeg?: string;
  // The empty starting leg of the sharing run this flow started (#455), created once
  // the flow commits to sending. A sharing run is the indefinite propagation of
  // a topic; one sharing flow starts one run and sends its first degree, so
  // every leg the flow sends is a child of this starting leg. Persisted so later
  // sends of the same flow (across the SMS handoff) reuse it.
  sharingRunId?: string;
  // Pre-send recipient selection (#479). The recipient box is otherwise
  // in-memory only, so it was lost whenever the ShareWizard outlet unmounted —
  // navigating to /profile to set names, or a mid-flow reload. Persisting it
  // lets a remount restore the exact selection. `confirmedContactIds` is the
  // subset already sent in a leg; on restore, the remaining recipients are
  // `recipients` minus those, so re-entering the compose steps after a
  // mid-leg reload shows only who's left to send to.
  recipients?: ContactList;
  confirmedContactIds?: string[];
  // "Exclude recipients who've seen this topic before" (#581). Persisted for
  // the same reason the selection above is: the checkbox is otherwise
  // in-memory only, so the set-names detour and a mid-flow reload would silently
  // flip the sender back to routing aware contacts to a reminder. Every flow
  // seeds it when it starts — see shareSessionSeed.
  excludeAware?: boolean;
}

export async function loadPersistedWizardState(
  topicId: string
): Promise<PersistedWizardState | null> {
  try {
    const raw = await StorageService.getShareWizardState(topicId);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as PersistedWizardState;
    if (parsed.schemaVersion !== STORAGE_SCHEMA_VERSION) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearPersistedWizardState(topicId: string): void {
  StorageService.clearShareWizardState(topicId).catch(() => undefined);
}

/**
 * A session as the wizard holds it in memory: the stored shape exactly, except
 * that the send queue is still full MessageDrafts — slimming those to
 * PersistedMessageDraft (dropping the Topic and its base64 image blobs, to stay
 * within the localStorage quota) is this function's job. Derived from
 * PersistedWizardState rather than restated, so a field added or renamed there
 * cannot silently stop being saved.
 */
export type WizardStateToPersist = Omit<PersistedWizardState, 'sendQueue'> & {
  sendQueue: MessageDraft[];
};

export async function savePersistedWizardState(
  topicId: string,
  state: WizardStateToPersist
): Promise<void> {
  const persisted: PersistedWizardState = {
    schemaVersion: state.schemaVersion,
    sendQueue: state.sendQueue.map(draft => ({
      header: {
        topicId: draft.header.topic.id,
        intent: draft.header.intent,
        type: draft.header.type,
        recipients: draft.header.recipients,
      },
      body: draft.body,
    })),
    sendQueueCursor: state.sendQueueCursor,
    inProgressLegId: state.inProgressLegId,
    updateSummary: state.updateSummary,
    aboutChangesSince: state.aboutChangesSince,
    previousLegId: state.previousLegId,
    connectionToPrevLeg: state.connectionToPrevLeg,
    sharingRunId: state.sharingRunId,
    recipients: state.recipients,
    confirmedContactIds: state.confirmedContactIds,
    excludeAware: state.excludeAware,
  };
  await StorageService.setShareWizardState(topicId, JSON.stringify(persisted));
}

/**
 * How a share session starts. Every entry point in the app goes through this.
 * A start answers two independent questions, and each part is present only
 * when its answer is yes:
 *   reshare   the sender is passing on a leg they received, named by
 *             `previousLegId`: "Keep the ball rolling & reshare" on the welcome
 *             sheet, and "Start the ball rolling & share" on the share dock.
 *   update    the session shares a change list, whose head is
 *             `aboutChangesSince`: "Share Updates", and the welcome sheet's
 *             "Keep the ball rolling & reshare" when the visitor arrived on an
 *             update. `updateSummary` is the summary the recipient received
 *             when the session also passes a leg on; a sender authoring their
 *             own changes omits it and writes one in the Changes step.
 *
 * Neither of them picks a recipient's intent. `routeRecipients` does that from
 * awareness: new people get an intro, aware people an update when the session
 * has a change list and a reminder otherwise. So all four combinations are
 * legal, and a reshare of an update sends `reshare` legs carrying the update.
 *
 * `typeDefaultsToExcludingAware` is the topic type's opening position for the
 * exclude-aware box, used only for a plain share: one with no leg to pass on
 * and no change list. The caller reads it with
 * `TopicLogic.defaultsToExcludingAwareRecipients` (this module knows nothing
 * about topic types).
 *
 * Field types come from PersistedWizardState rather than being restated, so a
 * rename or a type change there breaks here instead of quietly diverging. A
 * start is a strict subset of a stored session: the rest of one (the send
 * queue and cursor, in-progress leg, sharing run, recipient selection) is live
 * send state only the wizard writes, and a start seeds a session that has none
 * of it yet.
 */
export type ShareSessionStart = {
  /** Present when the sender is passing on a leg they received. */
  reshare?: Required<Pick<PersistedWizardState, 'previousLegId'>>;
  /** Present when the session shares a change list. */
  update?: Required<Pick<PersistedWizardState, 'aboutChangesSince'>> &
    Pick<PersistedWizardState, 'updateSummary'>;
  /** The topic type's opening exclude-aware position, used for a plain share. */
  typeDefaultsToExcludingAware?: boolean;
};

/** A start that shares a change list, and may pass a received leg on. */
export type ShareUpdatesStart = Required<Pick<ShareSessionStart, 'update'>> &
  Pick<ShareSessionStart, 'reshare'>;

/** A start that passes a received update on: a reshare carrying a change list. */
export type ReshareUpdatesStart = Required<ShareUpdatesStart>;

/**
 * What a start opens a session with: its parts' fields, and the opening
 * position it gives the exclude-aware box (#581) — the one place that rule
 * lives.
 *
 * A session with a change list starts unticked, because it exists to reach
 * people who already know — and the wizard withholds the checkbox there
 * entirely, so nothing can change it mid-flow. Otherwise a reshare starts
 * ticked: it passes one story on, and reaching people who have not seen the
 * topic is the point. A plain share takes its type's position.
 *
 * Whatever this seeds stands for the session unless the sender changes the box.
 *
 * Exported because the wizard itself needs the answer without the write: a
 * cold URL has no session to read, and the wizard owns storage from mount on,
 * so it asks what a plain share opens with and lets its own save
 * effect persist it.
 */
export function shareSessionSeed(
  start: ShareSessionStart
): Omit<
  PersistedWizardState,
  'schemaVersion' | 'sendQueue' | 'sendQueueCursor'
> {
  const { reshare, update } = start;
  return {
    ...(reshare && { previousLegId: reshare.previousLegId }),
    ...(update && {
      aboutChangesSince: update.aboutChangesSince,
      updateSummary: update.updateSummary,
    }),
    excludeAware: update
      ? false
      : reshare
        ? true
        : (start.typeDefaultsToExcludingAware ?? false),
  };
}

/**
 * Seed a fresh wizard session for a topic, then the caller routes into the
 * Share Wizard. Persisted state is the single carrier for which flow the wizard
 * is in and what that flow needs — there are no URL params for it — so this
 * writes the session the wizard reads back on mount, and is awaited so the
 * write lands before the navigation.
 *
 * Only a plain share yields to a send already in flight. It carries
 * no state of its own, so there is nothing it would lose by resuming, and an
 * in-progress leg or a queued send is a real send the user may be returning to
 * — clicking Share rather than the return-notification link. A start that
 * reshares a leg or carries a change list has state that has to take effect,
 * so it overwrites whatever it finds.
 *
 * A plain share otherwise replaces the stored session, which drops
 * an abandoned pre-send recipient selection. #479 persists that selection so
 * the set-names detour and a mid-flow reload restore it — desirable within a
 * session, but surprising when the user affirmatively starts a new share of the
 * same topic later.
 */
export async function startShareSession(
  topicId: string,
  start: ShareSessionStart
): Promise<void> {
  if (!start.reshare && !start.update) {
    const persisted = await loadPersistedWizardState(topicId);
    if (
      persisted &&
      (persisted.inProgressLegId || persisted.sendQueue.length)
    ) {
      return;
    }
  }
  await savePersistedWizardState(topicId, {
    schemaVersion: STORAGE_SCHEMA_VERSION,
    sendQueue: [],
    sendQueueCursor: 0,
    ...shareSessionSeed(start),
  });
}
